// n=8 élite — Catálogo versionado del informe (el vigente, v3: 3 pilares → 12
// conceptos → 44 subconceptos; el v2 era 4/21/60) y helpers de (de)serialización + cómputo de
// medias del JSONB `detailed_scores`. Sustituye al modelo de 2 niveles de
// `evaluationPillars.ts` (ver docs/journey/diseno-informe-n8-elite.md).
//
// INVARIANTE: una evaluación se lee SIEMPRE con su `catalog_version` guardado,
// nunca con el catálogo "actual".
//
// Hasta este PR el invariante estaba ESCRITO pero no lo sostenía el lenguaje:
// OCHO funciones de este módulo recorrían el `CATALOG` global sin recibir
// versión, y el único que protegía era el `throw` de `getCatalog`. En cuanto
// se enchufe un catálogo histórico ahí, ese throw deja de dispararse y esas
// ocho pasan a descartar en silencio las claves que el catálogo vigente no
// conoce — rellenándolas con DEFAULT_SCORE, que es peor que no pintar nada:
// fabrica notas que nadie puso.
//
// Ahora el catálogo VIAJA COMO PARÁMETRO y es obligatorio. No es una
// convención que haya que recordar: quien no lo pase, no compila.
//
// Forma en BD (detailed_scores), 3 niveles:
//   {
//     "catalog_version": 2,
//     "<pilar>": {
//       "score":   number,   // COMPUTADO = media de las medias de categoría (persistido)
//       "comment": string,   // comentario de pilar (uno de los DOS niveles de comentario)
//       "categories": {
//         "<cat>": {
//           "score": number, // COMPUTADO = media de subs (persistido)
//           "subs": { "<sub>": { "score": 1-10 } }   // lo ÚNICO que teclea el entrenador
//         }
//       }
//     }
//   }
// La fuente de verdad son las subs; category.score y pillar.score se recomputan
// SIEMPRE al guardar (withComputedAverages) y se persisten para lecturas baratas
// (visor, radar, subgráficos de evolución histórica).
// Comentarios: SOLO global (formal_evaluations.overall_comment) + por pilar
// (<pilar>.comment). Categorías y subcategorías NO llevan comentario.

import { CATALOGO_V2 } from './evaluationCatalogV2';
import { CATALOGO_V3 } from './evaluationCatalogV3';

export const CATALOG_VERSION = 3;
export const DEFAULT_SCORE = 5;
export const SCORE_MIN = 1;
export const SCORE_MAX = 10;

export interface SubcategoryDef {
  key: string;
  label: string;
}
export interface CategoryDef {
  key: string;
  label: string;
  subs: SubcategoryDef[];
}
export interface PillarDef {
  key: string;
  label: string;
  color: string;
  categories: CategoryDef[];
}

/**
 * Un catálogo con su versión pegada.
 *
 * La versión va DENTRO y no como argumento aparte a propósito: la escribe
 * `withComputedAverages` en el `catalog_version` del JSONB, y con dos valores
 * sueltos (un array por un lado, un número por otro) es cuestión de tiempo que
 * alguien selle una versión que no es la del contenido.
 */
export interface Catalogo {
  version: number;
  pilares: PillarDef[];
}

// ── Forma del JSONB ───────────────────────────────────────────────────────────
// `type` y no `interface` a propósito: TypeScript infiere una firma de índice
// implícita para los alias de tipo, pero NO para las interfaces. Sin eso, este
// árbol no es asignable al `Json` que piden las RPC generadas, y haría falta un
// cast justo en la frontera donde interesa que no lo haya.
export type SubScore = {
  score: number;
};
export type CategoryScore = {
  score: number; // computado (media de subs)
  subs: Record<string, SubScore>;
};
export type PillarBlock = {
  score: number; // computado (media de categorías)
  comment: string;
  categories: Record<string, CategoryScore>;
};
// Top-level: catalog_version + un bloque por pilar (keys: tecnico/tactico/fisico/actitud).
export interface DetailedScoresV2 {
  catalog_version: number;
  [pillarKey: string]: number | PillarBlock;
}

// ── Registro de versiones ─────────────────────────────────────────────────────
// Cada versión publicada vive en su fichero y se congela ahí. Este mapa es lo
// único que hay que tocar para añadir una: `getCatalog` (en evaluationReportV2)
// lo consulta, y deja de lanzar para las versiones que estén aquí.
export const CATALOGS: Record<number, Catalogo> = {
  [CATALOGO_V2.version]: CATALOGO_V2,
  [CATALOGO_V3.version]: CATALOGO_V3,
};

// Si `CATALOG_VERSION` sube y nadie registra su catálogo arriba, esto sería
// `undefined` y el fallo aparecería lejos: un formulario en blanco, un
// `totalSubcategories` que revienta, una media a 0. Mejor no arrancar.
if (!CATALOGS[CATALOG_VERSION]) {
  throw new Error(
    `El catálogo vigente (v${CATALOG_VERSION}) no está en CATALOGS. Registra su fichero antes de subir CATALOG_VERSION.`
  );
}

/** El catálogo vigente: con el que se puntúan las evaluaciones NUEVAS. */
export const CATALOGO_VIGENTE: Catalogo = CATALOGS[CATALOG_VERSION];

/**
 * Los pilares del catálogo vigente.
 *
 * Solo para lo que se está creando AHORA (el formulario en blanco, los conteos
 * de progreso). Para leer una evaluación guardada esto es la respuesta
 * equivocada: ahí va el catálogo de SU versión.
 */
export const CATALOG: PillarDef[] = CATALOGO_VIGENTE.pilares;

// ── Conteos derivados del catálogo ────────────────────────────────────────────
export function totalCategories(catalogo: Catalogo): number {
  return catalogo.pilares.reduce((n, p) => n + p.categories.length, 0);
}
export function totalSubcategories(catalogo: Catalogo): number {
  return catalogo.pilares.reduce(
    (n, p) => n + p.categories.reduce((m, c) => m + c.subs.length, 0),
    0
  );
}

// ── Utilidades internas ───────────────────────────────────────────────────────
function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
function round1(n: number): number {
  return Number(n.toFixed(1));
}
function clampScore(n: number): number {
  return Math.min(SCORE_MAX, Math.max(SCORE_MIN, n));
}
function asPillarBlock(v: number | PillarBlock | undefined): PillarBlock | undefined {
  return isObj(v) ? (v as PillarBlock) : undefined;
}

// ── Cómputo de medias ─────────────────────────────────────────────────────────
// Media (sin redondear) de las subcategorías presentes de una categoría.
export function categoryAvg(cat: CategoryScore | undefined, subKeys: string[]): number {
  if (!cat?.subs) return 0;
  const vals = subKeys
    .map((k) => cat.subs[k]?.score)
    .filter((s): s is number => typeof s === 'number');
  if (!vals.length) return typeof cat.score === 'number' ? cat.score : 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// Media (sin redondear) de las categorías de un pilar, recomputada desde subs.
export function pillarAvg(block: PillarBlock | undefined, cats: CategoryDef[]): number {
  if (!block?.categories) return 0;
  const vals = cats
    .map((c) =>
      categoryAvg(
        block.categories[c.key],
        c.subs.map((s) => s.key)
      )
    )
    .filter((v) => v > 0); // 0 = sin datos (las notas válidas son 1-10)
  if (!vals.length) return 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// ── Construcción / normalización ──────────────────────────────────────────────
export function buildEmptyDetailed(catalogo: Catalogo): DetailedScoresV2 {
  const ds: DetailedScoresV2 = { catalog_version: catalogo.version };
  for (const p of catalogo.pilares) {
    const categories: Record<string, CategoryScore> = {};
    for (const c of p.categories) {
      const subs: Record<string, SubScore> = {};
      for (const s of c.subs) subs[s.key] = { score: DEFAULT_SCORE };
      categories[c.key] = { score: DEFAULT_SCORE, subs };
    }
    ds[p.key] = { score: DEFAULT_SCORE, comment: '', categories };
  }
  return ds;
}

// Recalcula category.score = media(subs) y pillar.score = media(categorías) y los
// persiste. La fuente de verdad son las subs; nunca confía en un score tecleado.
export function withComputedAverages(ds: DetailedScoresV2, catalogo: Catalogo): DetailedScoresV2 {
  // La versión que se sella es la del catálogo con el que se computó, no la
  // vigente: si no, recalcular un informe viejo lo re-etiquetaría como nuevo.
  const out: DetailedScoresV2 = { catalog_version: catalogo.version };
  for (const p of catalogo.pilares) {
    const src = asPillarBlock(ds[p.key]);
    const categories: Record<string, CategoryScore> = {};
    for (const c of p.categories) {
      const subs: Record<string, SubScore> = {};
      for (const s of c.subs) {
        const sc = src?.categories?.[c.key]?.subs?.[s.key]?.score;
        subs[s.key] = { score: typeof sc === 'number' ? clampScore(sc) : DEFAULT_SCORE };
      }
      categories[c.key] = {
        score: round1(
          categoryAvg(
            { score: 0, subs },
            c.subs.map((s) => s.key)
          )
        ),
        subs,
      };
    }
    out[p.key] = {
      score: round1(pillarAvg({ score: 0, comment: '', categories }, p.categories)),
      comment: typeof src?.comment === 'string' ? src.comment : '',
      categories,
    };
  }
  return out;
}

// Normaliza un detailed_scores crudo de BD: confía SOLO en las subs (clampeadas)
// y los comentarios de pilar; recomputa todas las medias. Tolera filas parciales
// o de otra forma rellenando con DEFAULT_SCORE.
export function normalizeDetailed(raw: unknown, catalogo: Catalogo): DetailedScoresV2 {
  const base = buildEmptyDetailed(catalogo);
  if (isObj(raw)) {
    for (const p of catalogo.pilares) {
      const src = raw[p.key];
      if (!isObj(src)) continue;
      const block = asPillarBlock(base[p.key]);
      if (!block) continue;
      if (typeof src.comment === 'string') block.comment = src.comment;
      const srcCats = isObj(src.categories) ? src.categories : {};
      for (const c of p.categories) {
        const sc = srcCats[c.key];
        if (!isObj(sc)) continue;
        const srcSubs = isObj(sc.subs) ? sc.subs : {};
        for (const s of c.subs) {
          const sv = srcSubs[s.key];
          if (isObj(sv) && typeof sv.score === 'number') {
            block.categories[c.key].subs[s.key] = { score: clampScore(sv.score) };
          }
        }
      }
    }
  }
  return withComputedAverages(base, catalogo);
}

// Lo mismo que `normalizeDetailed` PERO SIN INVENTAR: las subs que no están en
// la fila se quedan fuera, y las medias se computan solo con lo que sí está
// (0 = sin datos, la misma convención que el formulario en vivo).
//
// Por qué hacen falta las dos. Rellenar con DEFAULT_SCORE es lo correcto para
// PINTAR un informe publicado —donde las 44 están— y es una trampa para ABRIR
// un borrador: una asignación recién creada volvería del servidor con las 44
// puestas a 5, el formulario diría «44/44 puntuadas», habilitaría «Publicar» y
// el gate del servidor tampoco lo pararía, porque esos cincos están escritos en
// la fila de verdad. Un informe entero que nadie puntuó, a un clic del alumno.
//
// Regla: para LEER lo que hay que puntuar, esta. Para PINTAR lo entregado,
// `normalizeDetailed`. Ver runbook §126.
export function normalizeDetailedSinRelleno(raw: unknown, catalogo: Catalogo): DetailedScoresV2 {
  const out: DetailedScoresV2 = { catalog_version: catalogo.version };
  const fila = isObj(raw) ? raw : {};
  for (const p of catalogo.pilares) {
    const src = isObj(fila[p.key]) ? (fila[p.key] as Record<string, unknown>) : null;
    const srcCats = src && isObj(src.categories) ? (src.categories as Record<string, unknown>) : {};
    const categories: Record<string, CategoryScore> = {};
    for (const c of p.categories) {
      const sc = isObj(srcCats[c.key]) ? (srcCats[c.key] as Record<string, unknown>) : null;
      const srcSubs = sc && isObj(sc.subs) ? (sc.subs as Record<string, unknown>) : {};
      const subs: Record<string, SubScore> = {};
      for (const s of c.subs) {
        const sv = srcSubs[s.key];
        // Clampeada igual que en `normalizeDetailed`: la fila viene de la base y
        // un 99 escrito a mano no puede entrar en el formulario.
        if (isObj(sv) && typeof sv.score === 'number') {
          subs[s.key] = { score: clampScore(sv.score) };
        }
      }
      categories[c.key] = {
        score: round1(
          categoryAvg(
            { score: 0, subs },
            c.subs.map((s) => s.key)
          )
        ),
        subs,
      };
    }
    out[p.key] = {
      score: round1(pillarAvg({ score: 0, comment: '', categories }, p.categories)),
      comment: src && typeof src.comment === 'string' ? src.comment : '',
      categories,
    };
  }
  return out;
}

// Media global del titular = media de las medias de pilar del catálogo dado.
export function overall(ds: DetailedScoresV2, catalogo: Catalogo): number {
  const vals = catalogo.pilares
    .map((p) => pillarAvg(asPillarBlock(ds[p.key]), p.categories))
    .filter((v) => v > 0);
  if (!vals.length) return 0;
  return round1(vals.reduce((a, b) => a + b, 0) / vals.length);
}

// ── Estado / completitud ──────────────────────────────────────────────────────
export function countSubsScored(ds: unknown, catalogo: Catalogo): number {
  if (!isObj(ds)) return 0;
  let n = 0;
  for (const p of catalogo.pilares) {
    const block = asPillarBlock(ds[p.key] as number | PillarBlock | undefined);
    if (!block) continue;
    for (const c of p.categories) {
      const cat = block.categories?.[c.key];
      if (!cat?.subs) continue;
      for (const s of c.subs) {
        if (typeof cat.subs[s.key]?.score === 'number') n++;
      }
    }
  }
  return n;
}

export type ReportStatus = 'not_started' | 'in_progress' | 'completed';

export function reportStatus(ds: unknown, catalogo: Catalogo): ReportStatus {
  const n = countSubsScored(ds, catalogo);
  if (n === 0) return 'not_started';
  if (n >= totalSubcategories(catalogo)) return 'completed';
  return 'in_progress';
}
